import { defineStore } from 'pinia'
import { getCurrentAdmin, loginAdmin, logoutAdmin } from '@/services/auth'
import type { AdminLoginRequest, AdminLoginResponse, CurrentAdminUser } from '@/types/auth'
import { clearAuthSession, readAuthSession, saveAuthSession } from '@/utils/authSession'

function initialSession() {
  const session = readAuthSession()
  return {
    tokenName: session?.tokenName || '',
    tokenValue: session?.tokenValue || '',
    tokenTimeout: session?.timeout || 0
  }
}

export const useAuthStore = defineStore('auth', {
  state: () => ({
    ...initialSession(),
    currentUser: null as CurrentAdminUser | null,
    sessionChecked: false
  }),
  getters: {
    isAuthenticated: state => Boolean(state.tokenValue),
    displayName: state => state.currentUser?.displayName || state.currentUser?.username || ''
  },
  actions: {
    async login(payload: AdminLoginRequest) {
      const session = await loginAdmin(payload)
      this.applySession(session)

      try {
        await this.fetchCurrentUser(true)
      } catch (error) {
        this.clearSession()
        throw error
      }
    },
    async fetchCurrentUser(force = false) {
      if (!this.tokenValue) throw new Error('登录状态不存在')
      if (!force && this.sessionChecked && this.currentUser) return this.currentUser

      try {
        const user = await getCurrentAdmin()
        this.currentUser = user
        this.sessionChecked = true
        return user
      } catch (error) {
        this.clearSession()
        throw error
      }
    },
    async logout() {
      try {
        if (this.tokenValue) await logoutAdmin()
      } finally {
        this.clearSession()
      }
    },
    applySession(session: AdminLoginResponse) {
      this.tokenName = session.tokenName
      this.tokenValue = session.tokenValue
      this.tokenTimeout = session.timeout
      this.currentUser = null
      this.sessionChecked = false
      saveAuthSession(session)
    },
    clearSession() {
      this.tokenName = ''
      this.tokenValue = ''
      this.tokenTimeout = 0
      this.currentUser = null
      this.sessionChecked = false
      clearAuthSession()
    }
  }
})
